import { Box, FormLabel, Input, styled, Typography } from '@mui/material'
import { Skeleton } from '@mui/material'
import TreeNode from 'components/Filters/EidFilter/GenericFilter/TreeViewWithSearch/components/TreeWithSearch/TreeNode'
import { useDynamicFormTreeTree, useTreeFilterAPI } from 'hooks'
import { isNilOrEmpty, useDebounce } from 'packages/core'
import { ArrowIcon, Icon } from 'packages/eid-icons'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSubcomponents from 'useSubcomponents'
import { TREE_VIEW_LEVELS } from 'utils'

export const StyledTreeContainer = styled(Box)({
    maxHeight: '20rem',
    overflowY: 'auto',
    width: '100%',
    position: 'absolute',
    top: '6rem',
    background: '#fff',
    borderRadius: '0.5rem',
    zIndex: '9',
    padding: '1rem',
    boxShadow: '0 0.4rem 0.6rem 0 #00000012',
    border: '0.1rem solid #d4d4d8',
    '& div': {
        marginTop: '0',
    },
    '& .MuiInputBase-adornedEnd': {
        width: '100%',
        marginBottom: '2rem',
        padding: '0.4rem 1rem',
        boxShadow: '0 0.4rem 0.6rem 0 #00000012',
        border: '0.1rem solid #d4d4d8',
        borderRadius: '0.5rem',

        '&:before': {
            border: 'none',
        },
    },
})

export const TreeSelect = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '0.1rem solid #d8d8dd',
    boxShadow: '#0000001a 0px 0.4rem 0.6rem -0.2rem',
    width: '100%',
    padding: '0.7rem 1.2rem',
    borderRadius: '0.5rem',
    height: '3.8rem',
    '& p': {
        fontSize: '1.4rem',
    },
    '& svg': {
        color: '#0000008a',
    },
    '&:hover': {
        borderColor: `${theme?.palette?.primary?.main} !important`,
    },
}))

export const TreeSelectRoot = styled(Box)({
    position: 'relative',
    width: '100%',
    '& label': {
        fontSize: '1.2rem',
        textTransform: 'uppercase',
        marginBottom: '0.8rem',
        display: 'block',
        color: '#b4b4b4',
    },
})

export const findObjectByValue = (obj, key, value) => {
    try {
        if (!obj || !value) {
            return null
        }

        if (obj[key] === value) {
            return obj
        }

        if (Array.isArray(obj.children)) {
            for (let i = 0; i < obj.children.length; i++) {
                const child = obj.children[i]
                const result = findObjectByValue(child, key, value)
                if (result) {
                    return result
                }
            }
        }

        for (let prop in obj) {
            if (typeof obj[prop] === 'object' && obj[prop] !== null) {
                const result = findObjectByValue(obj[prop], key, value)
                if (result) {
                    return result
                }
            }
        }
    } catch (err) {
        return null
    }

    return null
}

const MIN_CHARACTERS_REQUIRED_TO_SEARCH = 3

const FormTreeElement = ({
    item,
    errorMessage,
    handleSelect,
    value,
    personDetails,
}) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedValue, setSelectedvalue] = useState('')
    const debouncedSearchValue = useDebounce(searchTerm, 500)
    const [treeData, setTreeData] = useState({})
    const { hasAccessToControl } = useSubcomponents()

    const checkAccessToField = (fieldName) => {
        return hasAccessToControl(
            `ResourceAdmin-PersonDetails-${fieldName}-Control`,
        )
    }

    const { data: treeApiData, isFetching: loadingTree } = useTreeFilterAPI(
        `${item?.apiUrl}${
            item?.isLocationTree ? '&' : '?'
        }levelsToLoad=${TREE_VIEW_LEVELS}`,
        undefined,
        undefined,
        debouncedSearchValue?.length >= MIN_CHARACTERS_REQUIRED_TO_SEARCH
            ? debouncedSearchValue
            : '',
        true,
    )

    useEffect(() => {
        if (treeApiData) {
            setTreeData(treeApiData)
        } else if (loadingTree) {
            setTreeData({})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeApiData])

    useEffect(() => {
        if (value && treeData) {
            setSelectedvalue(findSelectedLabel(value))
        } else if (treeData && !value) {
            setSelectedvalue(`${t('Select')} ${t(item?.localeKey)}`)
        } else {
            setSelectedvalue(`${t('Loading')}...`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, treeData])

    const findSelectedLabel = (value) => {
        const selValue = findSelectedItem(value)
        const labelValue =
            item?.labelKey && personDetails ? personDetails[item?.labelKey] : ''
        return selValue || labelValue
    }

    const findSelectedItem = (id) => {
        const selectedNode = findObjectByValue(
            treeData,
            item?.dataAttributes?.value,
            id,
        )

        if (selectedNode && typeof selectedNode === 'object') {
            return selectedNode[item?.dataAttributes?.label]
        } else {
            return selectedNode
        }
    }

    const handleTreeSelect = (id, fieldName) => {
        setSelectedvalue(findSelectedItem(id))
        handleSelect(id, fieldName, findSelectedLabel(id))
        setIsOpen(!isOpen)
    }

    const updateParentData = (id, children) => {
        try {
            const appendChildren = (node, targetId, newChildren) => {
                if (!node) return node

                // Check if the current node matches the target ID
                if (node.id === targetId) {
                    return {
                        ...node,
                        children: [...(node.children || []), ...newChildren],
                        hasChildren: true, // Ensure `hasChildren` is updated
                    }
                }

                // Recursively process the children
                return {
                    ...node,
                    children: node.children?.map((child) =>
                        appendChildren(child, targetId, newChildren),
                    ),
                }
            }
            const updatedTreeData = appendChildren(treeData, id, children)
            setTreeData(updatedTreeData)
        } catch (err) {
            setTreeData(treeData)
        }
    }

    return (
        <Fragment>
            <TreeSelectRoot
                sx={
                    (item?.disabled || !checkAccessToField(item.fieldName)) && {
                        '& div': {
                            pointerEvents: 'none',
                            backgroundColor: '#f5f5f5 !important',
                            color: '#c1bcbc !important',
                        },
                    }
                }
                data-protectedsubcomponent={`ResourceAdmin-PersonDetails-${item.fieldName}-Control`}
            >
                {item?.localeKey && (
                    <FormLabel
                        sx={
                            !isNilOrEmpty(errorMessage) && {
                                color: 'red !important',
                                '& label': {
                                    color: 'red !important',
                                },
                            }
                        }
                        component="label"
                    >{`${t(item?.localeKey)} ${
                        item?.required &&
                        checkAccessToField(item.fieldName) &&
                        !item?.disabled
                            ? '*'
                            : ''
                    }`}</FormLabel>
                )}
                <TreeSelect
                    onClick={() =>
                        setIsOpen(
                            checkAccessToField(item.fieldName)
                                ? !isOpen
                                : isOpen,
                        )
                    }
                >
                    <Typography>
                        {selectedValue || t(item?.localeKey)}
                    </Typography>
                    <ArrowIcon direction={isOpen ? 'up' : 'down'} />
                </TreeSelect>
                {isOpen && (
                    <StyledTreeContainer>
                        <Fragment>
                            <Fragment>
                                <Input
                                    endAdornment={<Icon name="Search" />}
                                    placeholder={t('Search')}
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                />
                                {loadingTree ? (
                                    <Skeleton height={20} />
                                ) : (
                                    <TreeNode
                                        handleSelect={(val) => {
                                            handleTreeSelect(
                                                val?.id,
                                                item?.fieldName,
                                            )
                                        }}
                                        explorer={treeData || {}}
                                        value={value}
                                        highlightText={searchTerm}
                                        titleProp={'friendlyName'}
                                        valueProp={'id'}
                                        url={`${item?.apiUrl}${
                                            item?.isLocationTree ? '&' : '?'
                                        }levelsToLoad=${TREE_VIEW_LEVELS}`}
                                        showCollapseIcon={false}
                                        showRadioToSelect={false}
                                        disableAssignableCheck={true}
                                        onChildrenUpdate={(id, children) =>
                                            updateParentData(id, children)
                                        }
                                    />
                                )}
                            </Fragment>
                        </Fragment>
                    </StyledTreeContainer>
                )}
            </TreeSelectRoot>
        </Fragment>
    )
}

export default FormTreeElement
